import {
  ApiOutlined,
  LockOutlined,
  SafetyCertificateOutlined,
  UserOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import {
  type AIAgent,
  getAgentId,
  getAgentName,
  getAgentOwnerEmail,
  getAgentPlatformLabel,
} from "@p0security/shared/types/ai-inventory";
import { Button, Typography } from "antd";
import moment from "moment";
import { memo, useCallback } from "react";
import {
  color,
  font,
  iconTile,
  numericStyle,
  platformAccent,
} from "theme/aiInventory";

import type { RiskSeverity } from "../graph/types.ts";
import type { AgentActivity } from "../hooks/useAgentActivity.ts";
import { PlatformIcon } from "../platforms/PlatformIcon.tsx";

export type AgentStats = {
  connectorSummary: { unmanaged: number; total: number };
  riskSummary: { high: number; total: number };
  severityCounts: Record<RiskSeverity, number>;
};

export type AgentRowProps = {
  agent: AIAgent;
  stats: AgentStats;
  activity: AgentActivity;
  onSelect: (agent: AIAgent) => void;
  onSecureUnmanaged: (agent: AIAgent) => void;
};

const getRowSubtitle = (agent: AIAgent): string => {
  const platform = getAgentPlatformLabel(agent);
  switch (agent.platform) {
    case "copilot-studio": {
      const parts = [platform, agent.environmentLabel];
      if (agent.data.createdon) {
        parts.push(`discovered ${moment(agent.data.createdon).fromNow()}`);
      }
      return parts.join(" · ");
    }
    case "local-agents": {
      const parts = [platform];
      if (agent.data.clientPlatform) parts.push(agent.data.clientPlatform);
      if (agent.data.version) parts.push(`v${agent.data.version}`);
      if (agent.data.createdAt) {
        parts.push(`registered ${moment(agent.data.createdAt).fromNow()}`);
      }
      return parts.join(" · ");
    }
  }
};

const numericCell: React.CSSProperties = {
  ...numericStyle,
  fontSize: 12,
  color: color.ink,
};

const cellRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  minWidth: 0,
};

export const AgentRow: React.FC<AgentRowProps> = memo(function AgentRow({
  agent,
  stats,
  activity,
  onSelect,
  onSecureUnmanaged,
}) {
  const { connectorSummary, riskSummary } = stats;
  const id = getAgentId(agent);
  const name = getAgentName(agent);
  const ownerEmail = getAgentOwnerEmail(agent) ?? "\u2014";
  const handleSelect = useCallback(() => onSelect(agent), [agent, onSelect]);
  const handleKey = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onSelect(agent);
      }
    },
    [agent, onSelect]
  );
  const handleSecure = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onSecureUnmanaged(agent);
    },
    [agent, onSecureUnmanaged]
  );
  const hasConnectors = connectorSummary.total > 0;
  const allManaged = hasConnectors && connectorSummary.unmanaged === 0;
  const accent = platformAccent[agent.platform];
  const { lastSessionAt, connectedServerIds } = activity;
  const connectedServerCount = connectedServerIds.length;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleSelect}
      onKeyDown={handleKey}
      aria-label={name}
      data-testid={`agent-row-${id}`}
      className="p0-agent-row"
      style={{
        width: "100%",
        padding: "12px 16px",
        borderRadius: 6,
        background: color.card,
        cursor: "pointer",
        display: "grid",
        gridTemplateColumns:
          "40px minmax(220px, 1.6fr) 180px 180px 180px 240px",
        gridTemplateRows: "auto auto",
        alignItems: "center",
        columnGap: 16,
        rowGap: 2,
        minHeight: 72,
        textAlign: "left",
        fontFamily: font.sans,
      }}
    >
      <div
        aria-hidden
        style={{
          gridRow: "1 / 3",
          width: 36,
          height: 36,
          borderRadius: "50%",
          background: color.mute,
          color: accent,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        <PlatformIcon platform={agent.platform} size={20} />
      </div>
      <Typography.Text
        style={{
          fontWeight: 500,
          fontSize: 14,
          color: color.ink,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          minWidth: 0,
        }}
      >
        {name}
      </Typography.Text>
      <div style={cellRowStyle}>
        <span style={iconTile("neutral")}>
          <UserOutlined aria-hidden />
        </span>
        <Typography.Text
          style={{
            fontSize: 12,
            color: color.inkMute,
            fontFamily: font.mono,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {ownerEmail}
        </Typography.Text>
      </div>
      <div style={cellRowStyle}>
        <span style={iconTile("amber")}>
          <ApiOutlined aria-hidden />
        </span>
        <Typography.Text style={numericCell}>
          {agent.platform === "local-agents" ? (
            <>
              <strong>{connectedServerCount}</strong> MCP{" "}
              {connectedServerCount === 1 ? "server" : "servers"}
            </>
          ) : (
            <>
              <strong>{connectorSummary.total}</strong> Total (
              {connectorSummary.unmanaged} Unmanaged)
            </>
          )}
        </Typography.Text>
      </div>
      <div style={cellRowStyle}>
        <span style={iconTile("red")}>
          <WarningOutlined aria-hidden />
        </span>
        <Typography.Text style={numericCell}>
          <strong>{riskSummary.total}</strong> Total ({riskSummary.high} High)
        </Typography.Text>
      </div>
      <div
        className="p0-agent-row__actions"
        style={{
          gridColumn: "6",
          gridRow: "1 / 3",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
        }}
      >
        {hasConnectors &&
          (allManaged ? (
            <span
              aria-label="All connectors secured"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                height: 32,
                padding: "0 15px",
                borderRadius: 6,
                border: "none",
                color: color.ink,
                background: color.mute,
                fontFamily: font.sans,
                fontSize: 14,
                fontWeight: 400,
                lineHeight: 1,
              }}
            >
              <SafetyCertificateOutlined
                style={{ fontSize: 14, color: color.integration }}
                aria-hidden
              />
              Fully managed
            </span>
          ) : (
            <Button type="link" icon={<LockOutlined />} onClick={handleSecure}>
              Manage now
            </Button>
          ))}
      </div>
      <div
        style={{
          gridColumn: "2 / 6",
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          marginTop: 2,
          minWidth: 0,
        }}
      >
        {lastSessionAt !== undefined && (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              fontFamily: font.mono,
              fontSize: 11,
              letterSpacing: "0.01em",
              color: color.integration,
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            <span
              aria-hidden
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: color.integration,
                boxShadow: `0 0 0 3px ${color.integrationBg}`,
                display: "inline-block",
              }}
            />
            active {moment(lastSessionAt).fromNow()}
          </span>
        )}
        <Typography.Text
          style={{
            fontSize: 11,
            color: color.inkMute,
            fontFamily: font.mono,
            letterSpacing: "0.01em",
            whiteSpace: "normal",
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {getRowSubtitle(agent)}
        </Typography.Text>
      </div>
    </div>
  );
});
